import React from 'react'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import DestinationListItem from './DestinationListItem'
import type { WrappedData, WrappedTotalsData } from '../../../types/wrapped'
import confettiAnimation from '../../../Animations/flex_confetti.json'
import WrappedLottieAnimation from '../../WrappedLottieAnimation'
import { rem } from 'polished'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import { useIntl, FormattedMessage } from 'react-intl'

const ConfettiOverlay = styled(WrappedLottieAnimation)`
  position: absolute;
  top: 33%;
  left: ${rem(-20)};
  right: ${rem(-20)};
  transform: translateY(-33%);
  z-index: ${zIndex.menuInModal};
  pointer-events: none;
`

interface Props {
  topDestinations: WrappedData['topDestinations']
  stats?: WrappedData['stats']
  phase: 'question' | 'answer'
  correctAnswerId: string
  selectedId: string | null
  onSelect: (destinationId: string) => void
  className?: string
  isTotalsMode?: boolean
  totalsData?: WrappedTotalsData
}

function QuizContent({
  topDestinations,
  stats,
  phase,
  correctAnswerId,
  selectedId,
  className,
  onSelect,
  isTotalsMode = false,
  totalsData,
}: Props) {
  const intl = useIntl()
  const isCorrect = selectedId === correctAnswerId

  const totalDestinationsSearched = totalsData?.stats.destinationsSearched ?? 0

  const title = isTotalsMode
    ? intl.formatMessage({ id: 'iAwwjHg5O4V6X9QnVEKG', defaultMessage: "Can you guess our travellers' top viewed destination of 2025?", description: 'Quiz question heading for totals mode in the traveller details form' })
    : intl.formatMessage({ id: 'pS9PcfBW6z_itfMDRwzJ', defaultMessage: 'Can you guess your top viewed destination of 2025?', description: 'Quiz question heading for user mode in the year-in-review feature' })

  const bodyText = isTotalsMode
    ? (
        <FormattedMessage
          id="sANDSJFU2a427oBoA3cT"
          defaultMessage="Our travellers searched <b>{count} destinations</b> last year."
          description="Body text about destinations searched in totals mode"
          values={{
            count: <FormatDecimal value={totalDestinationsSearched} />,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          }}
        />
      )
    : (
        <FormattedMessage
          id="-8IS2kfS6SYeb7fFDRLq"
          defaultMessage="You searched <b>{count} destinations</b> last year."
          description="Body copy text about destinations searched by user"
          values={{
            count: stats?.destinationsSearched || 3,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          }}
        />
      )

  return (
    <div className={className}>
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <Caption colour="highlight-primary" variant="small" format="uppercase">
            {intl.formatMessage({ id: 'VNMcf3z7IcGK1ROaMf19', defaultMessage: 'Top Viewed Destinations', description: 'Descriptive caption for top viewed destinations section' })}
          </Caption>
          <Heading variant="heading5">
            <b>{title}</b>
          </Heading>
          <BodyText variant="medium">
            {bodyText}
          </BodyText>
        </Group>
        <Group direction="vertical" gap={16}>
          {topDestinations.map((destination) => (
            <DestinationListItem
              key={destination.id}
              destination={destination}
              phase={phase}
              isCorrect={destination.id === correctAnswerId}
              isSelected={destination.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </Group>
      </Group>
      {isCorrect && <ConfettiOverlay animationData={confettiAnimation} position="bottom" />}
    </div>
  )
}

export default React.memo(QuizContent)
